import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ApplicantSwitcher } from './ApplicantSwitcher';
import { BothApplicantsChatPane } from './BothApplicantsChatPane';
import { ChatHeader } from './ChatHeader';
import { SingleChatPane } from './SingleChatPane';
import Spinner from '~/components/icons/Spinner';
import { ChatBlockedReason } from '~/features/whatsapp/components/ChatPanel/ChatBlockedReason';
import { useApplicantChats } from '~/features/whatsapp/hooks/useApplicantChats';
import { useApplicantStartChat } from '~/features/whatsapp/hooks/useApplicantStartChat';
import { useChatContextLoanRequestId } from '~/features/whatsapp/hooks/useChatContextLoanRequestId';
import { useChats } from '~/features/whatsapp/hooks/useChats';
import {
    leaveActiveChat,
    setApplicantFilter,
    switchApplicantChat,
    useChatPanelStore,
} from '~/features/whatsapp/stores/chatPanelStore';
import { deriveChatDisplayName } from '~/features/whatsapp/utils/deriveChatDisplayName';
import {
    APPLICANT_FILTERS,
    type ApplicantFilter,
    inferApplicantFilter,
    resolveChatIdForFilter,
} from '~/features/whatsapp/utils/resolveApplicantChats';

type Props = {
    chatId: string;
};

export const ChatView = ({ chatId }: Props) => {
    const { t } = useTranslation();
    const { data: chats, isFetching } = useChats();
    const chat = chats?.find((entry) => entry.id === chatId);
    const applicantFilter = useChatPanelStore.use.applicantFilter();
    const autoOpenTemplatePicker = useChatPanelStore.use.autoOpenTemplatePicker();
    const primaryLoanRequestId = useChatContextLoanRequestId(chatId);
    const applicantResolution = useApplicantChats(primaryLoanRequestId);
    const { kn2 } = useApplicantStartChat(primaryLoanRequestId);

    const waitingForStartedChat = Boolean(autoOpenTemplatePicker || isFetching);

    const dualApplicantResolution = applicantResolution?.hasCoApplicant ? applicantResolution : null;
    const showApplicantSwitcher = dualApplicantResolution != null;
    const activeFilter: ApplicantFilter = showApplicantSwitcher
        ? applicantFilter ?? inferApplicantFilter(chatId, dualApplicantResolution)
        : APPLICANT_FILTERS.KN1;
    const kn2ChatId = dualApplicantResolution?.kn2?.chatId ?? null;
    const kn2Payload = kn2.payload;

    // A chat's loanRequestIds can span multiple leads sharing one phone (e.g. a duplicate
    // application) - chat.applicantName is resolved backend-side per chat, not per lead, so it can
    // legitimately name the wrong one. Prefer the name applicantResolution already ties to this
    // exact chatId via the viewed lead's own data (unambiguous), falling back only if it's a chat
    // outside that lead's known applicant slots.
    const activeSlot =
        activeFilter === APPLICANT_FILTERS.KN2 ? dualApplicantResolution?.kn2 : dualApplicantResolution?.kn1;
    // Keep an explicitly opened chat on another number instead of redirecting to the slot's preferred chat.
    const effectiveChatIdForSinglePane =
        showApplicantSwitcher && !activeSlot?.chatIds.includes(chatId)
            ? resolveChatIdForFilter(activeFilter, dualApplicantResolution) ?? chatId
            : chatId;
    const effectiveChatForSinglePane = chats?.find((entry) => entry.id === effectiveChatIdForSinglePane);
    const kn1Match = applicantResolution?.kn1.chatIds.includes(effectiveChatIdForSinglePane)
        ? applicantResolution.kn1
        : null;
    const kn2Match = applicantResolution?.kn2?.chatIds.includes(effectiveChatIdForSinglePane)
        ? applicantResolution.kn2
        : null;
    // Prefer the active filter's slot: kn1 and kn2 can share a chatId.
    const applicantMatch = activeFilter === APPLICANT_FILTERS.KN2 ? kn2Match ?? kn1Match : kn1Match ?? kn2Match;
    const singlePaneDisplayName =
        applicantMatch?.displayName ??
        (effectiveChatForSinglePane ? deriveChatDisplayName(effectiveChatForSinglePane) : '');

    // Bail to list if chat drops out of getChats (unlink / ACL change).
    useEffect(() => {
        if (chats != null && !chat && !waitingForStartedChat) {
            leaveActiveChat();
        }
    }, [chats, chat, waitingForStartedChat]);

    useEffect(() => {
        if (!applicantResolution?.hasCoApplicant) {
            setApplicantFilter(null);
            return;
        }
        setApplicantFilter(inferApplicantFilter(chatId, applicantResolution));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatId, primaryLoanRequestId, applicantResolution?.hasCoApplicant]);

    const kn2StartAttemptedRef = useRef(false);
    const [kn2StartFailed, setKn2StartFailed] = useState(false);
    useEffect(() => {
        if (activeFilter !== APPLICANT_FILTERS.KN2 || kn2ChatId || !kn2Payload) {
            kn2StartAttemptedRef.current = false;
            setKn2StartFailed(false);
            return;
        }
        if (kn2StartAttemptedRef.current) return;
        kn2StartAttemptedRef.current = true;

        kn2.start()
            .then(({ chatId: startedChatId }) => {
                switchApplicantChat(startedChatId, APPLICANT_FILTERS.KN2, { autoOpenTemplatePicker: true });
            })
            // Command errors are already toasted by useStartChatMutation's onError. The ref stays
            // latched (deliberately not reset here) so this effect - which reruns every render -
            // cannot retry-loop; switching filters or re-entering the chat resets it above.
            .catch(() => setKn2StartFailed(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilter, kn2ChatId, kn2Payload, kn2]);

    const handleApplicantFilterChange = (filter: ApplicantFilter) => {
        if (!applicantResolution) return;
        const nextChatId = resolveChatIdForFilter(filter, applicantResolution);
        switchApplicantChat(nextChatId, filter);
    };

    if (chats == null || (!chat && waitingForStartedChat)) {
        return (
            <div className="flex h-full items-center justify-center">
                <Spinner />
            </div>
        );
    }

    if (!chat) {
        return null;
    }

    if (showApplicantSwitcher && activeFilter === APPLICANT_FILTERS.BOTH) {
        const headerDisplayName = dualApplicantResolution.kn2
            ? `${dualApplicantResolution.kn1.displayName} & ${dualApplicantResolution.kn2.displayName}`
            : dualApplicantResolution.kn1.displayName;

        return (
            <BothApplicantsChatPane
                kn1ChatId={dualApplicantResolution.kn1.chatId}
                kn2ChatId={dualApplicantResolution.kn2?.chatId ?? null}
                kn1Label={t(`WHATSAPP_CHAT.APPLICANT_SWITCHER.${APPLICANT_FILTERS.KN1}`)}
                kn2Label={t(`WHATSAPP_CHAT.APPLICANT_SWITCHER.${APPLICANT_FILTERS.KN2}`)}
                loanRequestIds={chat.loanRequestIds}
                activeApplicantFilter={activeFilter}
                onApplicantFilterChange={handleApplicantFilterChange}
                headerDisplayName={headerDisplayName}
                resolved={chat.resolved}
            />
        );
    }

    if (showApplicantSwitcher && activeFilter === APPLICANT_FILTERS.KN2 && !kn2ChatId) {
        return (
            <div className="flex h-full min-h-0 flex-col">
                <ChatHeader
                    canLink={false}
                    chatId={chatId}
                    displayName={dualApplicantResolution.kn2?.displayName ?? chat.customerIdentity}
                    lastInboundAt={null}
                    loanRequestIds={chat.loanRequestIds}
                    resolved={chat.resolved}
                />
                <ApplicantSwitcher activeFilter={activeFilter} onChange={handleApplicantFilterChange} />
                <div className="flex flex-1 items-center justify-center px-4">
                    {kn2Payload != null && !kn2StartFailed ? (
                        <Spinner />
                    ) : (
                        primaryLoanRequestId && (
                            <ChatBlockedReason loanRequestId={primaryLoanRequestId} applicantSlot={2} />
                        )
                    )}
                </div>
            </div>
        );
    }

    return (
        <SingleChatPane
            chatId={effectiveChatIdForSinglePane}
            displayName={singlePaneDisplayName}
            loanRequestIds={chat.loanRequestIds}
            showApplicantSwitcher={showApplicantSwitcher}
            activeApplicantFilter={activeFilter}
            onApplicantFilterChange={handleApplicantFilterChange}
        />
    );
};
